import lodash from 'lodash';
import {
    queryGraphQL,
    mutateGraphQL
} from 'common/util/query';
import {
    toQuery,
    toMutation,
    nodeWithArgs
} from 'common/util/queryHelper';
import {
    addApplicationMessage,
    addApplicationMessages
} from 'common/actions/applicationMessagesActions';
import { 
    binderInstitutionIdSelector
} from '../../common/selectors';
import { loadBinder } from 'binder/actions';

export const NOTIFY_BINDER_SETUP_LOADED = 'NOTIFY_BINDER_SETUP_LOADED';
export const NOTIFY_BINDER_ACTIVATION_SUCCESS = 'NOTIFY_BINDER_ACTIVATION_SUCCESS';
export const LOAD_BINDER_SETUP_FIELDS = 'LOAD_BINDER_SETUP_FIELDS';
export const UPDATE_BINDER_SETUP_FIELD_VALUE = 'UPDATE_BINDER_SETUP_FIELD_VALUE';
export const WRITE_BINDER_SETUP_ERROR_MESSAGES = 'WRITE_BINDER_SETUP_ERROR_MESSAGES';
export const BINDER_SETUP_WRITE_SAVING = 'BINDER_SETUP_WRITE_SAVING';
export const SET_BINDER_NOTIFIED_USERS_LOADED_STATUS = 'SET_BINDER_NOTIFIED_USERS_LOADED_STATUS';
export const LOAD_BINDER_NOTIFIED_USERS_SUCCESS = 'LOAD_BINDER_NOTIFIED_USERS_SUCCESS';
export const SAVE_BINDER_NOTIFIED_USERS_SUCCESS = 'SAVE_BINDER_NOTIFIED_USERS_SUCCESS';

function notifyBinderSetupLoaded(value) {
    return {
        type: NOTIFY_BINDER_SETUP_LOADED,
        payload: value
    };
}

function notifyBinderActivationSuccess() {
    return {
        type: NOTIFY_BINDER_ACTIVATION_SUCCESS
    };
}

function binderSetupFieldsAction(setupFields, descriptors) {
    return {
        type: LOAD_BINDER_SETUP_FIELDS,
        payload: {
            setupFields,
            descriptors
        }
    };
}

export function binderSetupFieldValueChange(groupId, value) {
    return {
        type: UPDATE_BINDER_SETUP_FIELD_VALUE,
        payload: {
            groupId,
            value
        }
    };
}

export function loadBinderSetupFields(predicateGroupsMap) {
    const predicateGroups = Array.from(predicateGroupsMap.values());

    return (dispatch, getState) => {
        dispatch(notifyBinderSetupLoaded(false));
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const query = toQuery(nodeWithArgs('descriptors', {
            institutionId
        }, [
            'id',
            'displayName',
            'dataType',
            'label',
            'instructions',
        ]));

        return queryGraphQL(query).then(response => {
            if (response.errors) {
                dispatch(notifyBinderSetupLoaded(true));
                // TODO: Show error message to the user.
            }

            dispatch(binderSetupFieldsAction(predicateGroups, response.data.descriptors));
        });
    };
}

export function resetBinderSetupFieldValues(predicateGroups) {
    return (dispatch, getState) => {
        const state = getState();
        const descriptors = lodash.get(state, 'binder.setup.descriptors');

        dispatch(binderSetupFieldsAction(predicateGroups, descriptors));
    };
}

export function saveBinderQuery(binder, serializedQueryNode, binderStatus='NEW') {
    const updatedBinder = lodash.cloneDeep(binder);
    const rootFolder = lodash.find(updatedBinder.layout.folders, folder => folder.parentId === null);
    rootFolder.query = JSON.parse(serializedQueryNode);
    const updatedBinderFields = lodash.pick(updatedBinder, ['title', 'description', 'layout', 'status']);
    updatedBinderFields.layout = JSON.stringify(updatedBinder.layout);
    updatedBinderFields.status = binderStatus;
    const mutation = toMutation(
        nodeWithArgs('saveBinder', {
            id: binder.id,
            binder: updatedBinderFields
        })
    );

    return dispatch => {
        return queryGraphQL(mutation).then(response => {
            dispatch(writeBinderSaving(null));
            if (response.errors) {
                dispatch(addApplicationMessages('error', lodash.map(response.errors, 'message')));
            } else if (binderStatus === 'ACTIVE') {
                dispatch(addApplicationMessage('success', 'Binder has been successfully activated'));
                const loadBinderPromise = dispatch(loadBinder(binder.id));
                loadBinderPromise.then(() => dispatch(notifyBinderActivationSuccess()));
            } else {
                dispatch(addApplicationMessage('success', 'Binder has been successfully saved'));
            }
        });
    };
}

export function displayBinderActivationError() {
    return addApplicationMessage('error', 'There are validation errors that need corrected before binder can be activated.');
}

export function writeSetupErrorMessages(messageObjects) {
    return {
        type: WRITE_BINDER_SETUP_ERROR_MESSAGES,
        payload: messageObjects
    };
}

export function writeBinderSaving(value = null) {
    return {
        type: BINDER_SETUP_WRITE_SAVING,
        payload: value
    };
}

export const loadBinderNotifiedUsers = binderId => async (dispatch) => {
    dispatch(setBinderNotifiedUsersLoadedStatusAction(false));
    const loadBinderNotifiedUsersQuery = nodeWithArgs('loadBinderNotifiedUsers', {
        binderId
    }, [
        'id',
        'emailAddress',
        'displayName'
    ]);

    const {
        errors,
        data: {
            loadBinderNotifiedUsers: binderNotifiedUsers
        }
    } = await queryGraphQL(toQuery(loadBinderNotifiedUsersQuery));
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to load binder notified users.'));
        return;
    }
    dispatch(loadBinderNotifiedUsersSuccessAction(binderNotifiedUsers));
    dispatch(setBinderNotifiedUsersLoadedStatusAction(true));
};

export const saveBinderNotifiedUsers = ({ binderId, userIds }) => async (dispatch) => {
    const saveBinderNotifiedUsersQuery = nodeWithArgs('saveBinderNotifiedUsers', {
        binderId,
        userIds,
    }, [
        'id',
        'emailAddress',
        'displayName'
    ]);

    const {
        errors,
        data: {
            saveBinderNotifiedUsers: binderNotifiedUsers
        }
    } = await mutateGraphQL(toMutation(saveBinderNotifiedUsersQuery));

    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to save binder notified users.'));
        return;
    }

    dispatch(saveBinderNotifiedUsersSuccessAction(binderNotifiedUsers));
};

export const setBinderNotifiedUsersLoadedStatusAction = loaded => ({
    type: SET_BINDER_NOTIFIED_USERS_LOADED_STATUS,
    payload: loaded
});

export const loadBinderNotifiedUsersSuccessAction = users => ({
    type: LOAD_BINDER_NOTIFIED_USERS_SUCCESS,
    payload: users
});

export const saveBinderNotifiedUsersSuccessAction = users => ({
    type: SAVE_BINDER_NOTIFIED_USERS_SUCCESS,
    payload: users
});
